import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Brain, 
  TrendingUp, 
  AlertTriangle, 
  Package, 
  Calendar, 
  Users, 
  Activity,
  Zap,
  Search,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';

const AIDashboard = () => {
  const [stats, setStats] = useState({
    aiQueries: 0,
    confidence: 0,
    activeUsers: 0,
    alerts: 0
  });
  
  const [recentActivity, setRecentActivity] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [quickInsights, setQuickInsights] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // データ取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        // モックデータ（実際の実装では適切なAPIを呼び出し）
        setStats({
          aiQueries: 247,
          confidence: 87,
          activeUsers: 12,
          alerts: 3
        });

        setRecentActivity([
          {
            id: 1,
            user: '佐藤看護師',
            action: 'バイポーラ鉗子の在庫を確認',
            timestamp: new Date(Date.now() - 5 * 60 * 1000),
            type: 'inventory',
            status: 'success'
          },
          {
            id: 2,
            user: '田中技師',
            action: '腹腔鏡手術の準備手順を質問',
            timestamp: new Date(Date.now() - 15 * 60 * 1000),
            type: 'procedure',
            status: 'success'
          },
          {
            id: 3,
            user: 'システム',
            action: '在庫アラート: ハーモニック残り3個',
            timestamp: new Date(Date.now() - 30 * 60 * 1000),
            type: 'alert',
            status: 'warning'
          },
          {
            id: 4,
            user: '山田医師',
            action: '緊急手術の器械準備について相談',
            timestamp: new Date(Date.now() - 45 * 60 * 1000),
            type: 'emergency',
            status: 'emergency'
          }
        ]);

        setPredictions([
          {
            id: 1,
            item: 'マイクロバイポーラ鉗子',
            currentStock: 5,
            predictedDepletion: '3日後',
            confidence: 92,
            urgency: 'high'
          },
          {
            id: 2,
            item: 'ハーモニック',
            currentStock: 3,
            predictedDepletion: '2日後',
            confidence: 89,
            urgency: 'critical'
          },
          {
            id: 3,
            item: '5mmトロッカー',
            currentStock: 15,
            predictedDepletion: '12日後',
            confidence: 78,
            urgency: 'medium'
          }
        ]);

        setQuickInsights([
          {
            id: 1,
            title: '月曜日の準備時間',
            insight: '平均20%長い傾向',
            action: 'スタッフ配置の見直し推奨',
            confidence: 92
          },
          {
            id: 2,
            title: '緊急手術パターン',
            insight: '14-16時に集中',
            action: '緊急セットの事前準備',
            confidence: 85
          },
          {
            id: 3,
            title: '在庫効率化',
            insight: '発注タイミングの最適化で15%改善',
            action: '自動発注の導入検討',
            confidence: 88
          }
        ]);

        setIsLoading(false);
      } catch (error) {
        console.error('データ取得エラー:', error);
        setIsLoading(false);
      }
    };

    fetchData();
    
    // 5分ごとに更新
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 活動タイプのアイコン取得
  const getActivityIcon = (type, status) => {
    const iconClass = "w-4 h-4";
    
    if (status === 'emergency') {
      return <AlertTriangle className={`${iconClass} text-red-500`} />;
    }
    
    switch (type) {
      case 'inventory':
        return <Package className={`${iconClass} text-blue-500`} />;
      case 'procedure':
        return <Activity className={`${iconClass} text-green-500`} />;
      case 'alert':
        return <AlertTriangle className={`${iconClass} text-yellow-500`} />;
      case 'emergency':
        return <AlertTriangle className={`${iconClass} text-red-500`} />;
      default:
        return <Info className={`${iconClass} text-gray-500`} />;
    }
  };

  // 緊急度のバッジスタイル
  const getUrgencyBadge = (urgency) => {
    const baseClass = "px-2 py-1 rounded-full text-xs font-medium";
    switch (urgency) {
      case 'critical':
        return `${baseClass} bg-red-100 text-red-800`;
      case 'high':
        return `${baseClass} bg-orange-100 text-orange-800`;
      case 'medium':
        return `${baseClass} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800`;
    }
  };

  // 時間表示フォーマット
  const formatTimeAgo = (timestamp) => {
    const diff = Date.now() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return '今';
    if (minutes < 60) return `${minutes}分前`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}時間前`;
    
    const days = Math.floor(hours / 24);
    return `${days}日前`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">ダッシュボードを読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* ヘッダー */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">ScrubEdge AI ダッシュボード</h1>
              <p className="text-gray-600 mt-1">手術室運営の AI アシスタント統合管理</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-gray-500">最終更新</div>
                <div className="font-medium">{new Date().toLocaleTimeString('ja-JP')}</div>
              </div>
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* 統計カード */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <MessageSquare className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">今日のAI質問</p>
                <p className="text-2xl font-bold text-gray-900">{stats.aiQueries}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <Brain className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">AI信頼度</p>
                <p className="text-2xl font-bold text-gray-900">{stats.confidence}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">アクティブユーザー</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">緊急アラート</p>
                <p className="text-2xl font-bold text-gray-900">{stats.alerts}</p>
              </div>
            </div>
          </div>
        </div>

        {/* メインコンテンツ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 予測分析 */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center">
                <TrendingUp className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold">在庫予測分析</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {predictions.map(prediction => (
                  <div key={prediction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{prediction.item}</h3>
                      <p className="text-sm text-gray-600">
                        在庫: {prediction.currentStock}個 | {prediction.predictedDepletion}で枯渇予測
                      </p>
                      <div className="flex items-center mt-1">
                        <div className="text-xs text-gray-500">信頼度: {prediction.confidence}%</div>
                        <span className={`ml-2 ${getUrgencyBadge(prediction.urgency)}`}>
                          {prediction.urgency === 'critical' ? '緊急' : 
                           prediction.urgency === 'high' ? '高' : '中'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 最近の活動 */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center">
                <Activity className="w-5 h-5 text-green-600 mr-2" />
                <h2 className="text-xl font-semibold">最近の活動</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentActivity.map(activity => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    {getActivityIcon(activity.type, activity.status)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.user}</p>
                      <p className="text-sm text-gray-600">{activity.action}</p>
                      <p className="text-xs text-gray-400">{formatTimeAgo(activity.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* AIインサイト */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center">
                <Zap className="w-5 h-5 text-yellow-600 mr-2" />
                <h2 className="text-xl font-semibold">AIインサイト</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {quickInsights.map(insight => (
                  <div key={insight.id} className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                    <h3 className="font-medium text-gray-900">{insight.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{insight.insight}</p>
                    <p className="text-sm text-blue-600 mt-2">💡 {insight.action}</p>
                    <div className="text-xs text-gray-500 mt-1">信頼度: {insight.confidence}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* クイックアクション */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">クイックアクション</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button className="flex items-center justify-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
              <MessageSquare className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-sm font-medium">AI チャット</span>
            </button>
            
            <button className="flex items-center justify-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
              <Search className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-sm font-medium">知識検索</span>
            </button>
            
            <button className="flex items-center justify-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
              <BarChart3 className="w-5 h-5 text-purple-600 mr-2" />
              <span className="text-sm font-medium">詳細分析</span>
            </button>
            
            <button className="flex items-center justify-center p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
              <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-sm font-medium">緊急対応</span>
            </button>
          </div>
        </div>

        {/* システム状態 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">システム状態</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <span className="font-medium">AI エンジン</span>
              </div>
              <span className="text-sm text-green-600">正常</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <span className="font-medium">知識ベース</span>
              </div>
              <span className="text-sm text-green-600">正常</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-yellow-600 mr-2" />
                <span className="font-medium">API制限</span>
              </div>
              <span className="text-sm text-yellow-600">78% 使用</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIDashboard;